/**
 * @file   src\components\EditCardModal.tsx
 * @brief  Edit Card Modal component.
 * @date   Jun, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { React, useState, Offcanvas, Button, useEffect, Modal } from '../utils/thirdpartyComponents';
import CWInput from './CWInput';
import '../components/OffCanvas.scss';
import Cardicon from "../assets/images/credit-card-icon.svg";
import { YEAR_LIMIT, MONTH_LIMIT } from '../utils/constants';
import { updatePaymentSchema } from 'src/validations/updatePaymentSchema';
import * as Yup from 'yup';
import { UpdatePaymentApi } from 'src/utils/apiUrls';
import axios from 'src/config/axiosConfig';
import SmallLoader from './SmallLoader';
import { MessageToaster } from "src/utils/toastUtil";

// Toast object creation.

const EditCardModal = (props: any) => {
const { show, onHide } = props;

const [loading, setLoading] = useState(false);
// Instantiate MessageToaster
const toaster = new MessageToaster();

const handleOnHide = () => {
    onHide();
}

// Initialize states for formdata
const [formData, setFormData] = useState({
    paymentMethodId: '',
    isDefault: 0,
    action: "edit",
    exp_month: 0,
    exp_year: 0,
})

// Initialise states for errors
const [errors, setErrors] = useState({
    paymentMethodId: '',
    isDefault: '',
    action: '',
    exp_month: '',
    exp_year: '',
});


useEffect(() => {
    setFormData({
        paymentMethodId: props.editcardid,
        isDefault: props.edit_default,
        action: "edit",
        exp_month: props.edit_exp_month,
        exp_year: props.edit_exp_year,
    });
}, [props]);

// Checkbox change handler
const handleCheckboxChange = (checked: boolean, name: string) => {
    // Update form data with the checkbox state
    setFormData({
        ...formData,
        [name]: checked === true ? 1 : 0,  // Assign the number value directly
    });
};

// Triggers whenever input is entered
const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {

    // To allow only digits in exp_month field
    if (event.target.name === "exp_month") {
        // Extract only digits from the input value
        const exp_month_value = event.target.value;
        if (!/^\d*$/.test(exp_month_value)) {
            event.target.value = exp_month_value.replace(/\D/g, '');
        }
    }

    // To allow only digits in exp_month field
    if (event.target.name === "exp_year") {
        // Extract only digits from the input value
        const exp_year_value = event.target.value;
        if (!/^\d*$/.test(exp_year_value)) {
            event.target.value = exp_year_value.replace(/\D/g, '');
        }
    }

    const { name, value } = event.target

    // To remove validation when user enters input
    setErrors({
        paymentMethodId: '',
        isDefault: '',
        action: '',
        exp_month: '',
        exp_year: '',
    });

    setFormData({
        ...formData,
        [name]: Number(value),
    });
};

// Triggers when submit
const handleSubmit = async () => {

    try {
        // Validate form data
        await updatePaymentSchema.validate(formData, { abortEarly: false });
        // API call to update card details
        try {
            setLoading(true);
            toaster.toastDismiss();
            const { data } = await axios.post(UpdatePaymentApi.UPDATE_CARD_DETAILS,formData);
            if(data?.error === 0)
            {
                //Clear errors
                setErrors({
                    paymentMethodId: '',
                    isDefault: '',
                    action: '',
                    exp_month: '',
                    exp_year: '',
                });
                handleOnHide();
                toaster.toastSuccess("Card Updated successfully.");
            }
            else
            {
                // show error message
                toaster.toastError(data?.message);
            }
            } catch (error: any) {
                toaster.toastError(error?.response?.data?.message || error?.message);
            }
            finally{
                setLoading(false);
            }
    } catch (error) {

        //If validation fails on submit, then errors are shown
        if (error instanceof Yup.ValidationError) {
            const newErrors: any = {};
            error.inner.forEach((err) => {
                newErrors[err.path as keyof typeof formData] = err.message;
            });
            setErrors(newErrors);
        }
    }
}

const [showDeleteModal, setShowDeleteModal] = useState(false);
const handleCloseDeleteModal = () => setShowDeleteModal(false);
const handleShowDeleteModal = () => setShowDeleteModal(true);

const Ondeletecard = async () => {
    try {
        setLoading(true); // Show loader
        const deleteCardRequest = {
            data: {
                paymentMethodId: props.editcardid,
            },
        };
        const responseCardDelete = await axios.delete(UpdatePaymentApi.DELETE_CARD_DETAILS, deleteCardRequest);
        
        if (responseCardDelete?.data?.error === 0) {
            toaster.toastSuccess("Card deleted successfully.");
            handleCloseDeleteModal();
            handleOnHide();
        } else {
            toaster.toastError(responseCardDelete?.data?.message);
        }
    } catch (error) {
        toaster.toastError("Failed to delete the card. Please try again.");
    } finally {
        setLoading(false); // Hide loader
    }
};

  
  return (
    <div>
    <Offcanvas 
        id="editcard-offcanvas"
        show={show} 
        onHide={()=>handleOnHide()} 
        {...props} 
        placement="bottom"
        data-bs-backdrop="static"
    >
    <Offcanvas.Header className='flex-column align-items-start'>
        <div className="title-sec d-flex align-items-center justify-content-between w-100">
            <h1>Edit Card</h1>
            <div className="location-info-card setprimary-btn position-relative">
                <label className="radio-wrap m-0">
                    <input
                        name="isDefault"
                        type="checkbox"
                        className='input-radio'
                        value={formData.isDefault}
                        checked={formData.isDefault === 1 ? true : false}
                        onChange={(e) => 
                        {
                            if(props.edit_default === 0) {handleCheckboxChange(e.target.checked, 'isDefault');}
                        }
                    }
                    />
                    <span>
                        <div className="text-info">
                            <p>Set as Primary Card</p>
                        </div>
                    </span>
                </label>
            </div>
        </div>
        <div className="sub-title-sec d-flex align-items-center justify-content-between w-100">
            <p>{`**** **** **** ${props.editcardnumber}`}</p>
            {formData.isDefault === 0 &&(
                <Button variant="secondary" className="secondary-btn d-flex align-items-center" onClick={handleShowDeleteModal}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="11.54" height="12.89" viewBox="0 0 11.54 12.89">
                        <defs>
                            <clipPath id="clip-path">
                                <rect id="Rectangle_290" data-name="Rectangle 290" width="11.54" height="12.89" transform="translate(0 0)" fill="#fff" />
                            </clipPath>
                        </defs>
                        <g id="Group_3906" data-name="Group 3906" transform="translate(0 0)">
                            <g id="Group_3906-2" data-name="Group 3906" transform="translate(0 0)" clip-path="url(#clip-path)">
                                <path id="Path_647" data-name="Path 647" d="M10.966,13.444H.572a.573.573,0,1,1,0-1.146H10.966a.573.573,0,0,1,0,1.146" transform="translate(0.001 -10.327)" fill="#fff" />
                                <path id="Path_648" data-name="Path 648" d="M15.648,13.456v7.828a.843.843,0,0,1-.843.841H9.67a.842.842,0,0,1-.841-.841V13.456Zm1.1-1.122H7.725a2.08,2.08,0,0,0-.018.266v8.683A1.962,1.962,0,0,0,9.67,23.247h5.135a1.964,1.964,0,0,0,1.965-1.963V12.6a2.08,2.08,0,0,0-.018-.266" transform="translate(-6.472 -10.357)" fill="#fff" />
                                <path id="Path_649" data-name="Path 649" d="M24.3,34.538h0a.644.644,0,0,1-.644-.644V30.006a.644.644,0,1,1,1.289,0v3.888a.644.644,0,0,1-.644.644" transform="translate(-19.868 -24.656)" fill="#fff" />
                                <path id="Path_650" data-name="Path 650" d="M40.261,34.538h0a.644.644,0,0,1-.644-.644V30.006a.644.644,0,1,1,1.289,0v3.888a.644.644,0,0,1-.644.644" transform="translate(-33.267 -24.656)" fill="#fff" />
                                <path id="Path_651" data-name="Path 651" d="M23.915.644h0A.644.644,0,0,1,24.559,0H27.11a.644.644,0,0,1,0,1.289H24.559a.644.644,0,0,1-.644-.644" transform="translate(-20.082 0)" fill="#fff" />
                            </g>
                        </g>
                    </svg>
                    Delete Card
                </Button>
            )}
        </div>
    </Offcanvas.Header>
    <Offcanvas.Body>
        <div className="card-details">
            <div className="title-sec d-flex align-items-center">
                    <span><img src={Cardicon} alt="Car Wash App" /></span>
                    <h6>Edit Card Details</h6>
            </div>
            <div className="card-form d-flex flex-column">
                <CWInput
                    id="cardnumber"
                    name="cardnumber"
                    type="text"
                    label="Card Number"
                    value={`**** **** **** ${props.editcardnumber}`}
                    isMandatory
                    disabled
                />
                <div className="multi-col d-grid">
                    <CWInput
                        id="exp_month"
                        name="exp_month"
                        type="text"
                        inputmode="numeric" // This helps mobile devices to show a numeric keypad
                        label="Expiry Month"
                        isMandatory
                        value={formData.exp_month}
                        errorMessage={errors.exp_month && errors.exp_month}
                        onChange={handleInputChange}
                        maxLength={MONTH_LIMIT}
                        autoFocus={true}
                    />
                    <CWInput
                        id="exp_year"
                        name="exp_year"
                        type="text"
                        inputmode="numeric" // This helps mobile devices to show a numeric keypad
                        label="Expiry Year"
                        value={formData.exp_year}
                        errorMessage={errors.exp_year && errors.exp_year}
                        onChange={handleInputChange}
                        maxLength={YEAR_LIMIT}
                        isMandatory
                    />
                </div>
            </div>
        </div>
    </Offcanvas.Body>
    <div className="offcanvas-footer d-grid">
        <Button variant="light" onClick={handleOnHide}>Cancel</Button>
        <Button variant="primary" className="primary-btn btn-main" onClick={handleSubmit}>Save</Button>
    </div>
  </Offcanvas>
  <Modal 
    show={showDeleteModal} 
    onHide={handleCloseDeleteModal}
    centered
    className='notification-modal'
  >
  <Modal.Body className='text-center'>
    <h6>Are you sure you want to delete?</h6>
  </Modal.Body>
  <Modal.Footer className='d-grid'>
    <Button variant="light" className="m-0" onClick={handleCloseDeleteModal}>No</Button>
    <Button variant="primary" className='primary-btn btn-main m-0' onClick={Ondeletecard}>Yes</Button>
  </Modal.Footer>
</Modal>
{loading ? <SmallLoader/> : ""}
</div>
  );
};
export default EditCardModal;
